import React, { useState, useEffect } from "react";
import { modalBoxStyle } from "../styles";
import {
  Typography,
  Box,
  Modal,
  Grid,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useMutation, useQueryClient } from "react-query";
import inventoryClient from "../../../clients/inventoryClient";

const EditStockCountRecordQuantityModal = ({
  isOpen,
  handleCloseEditQuantityModal,
  record,
  stockCountId,
  establishmentId,
  onSuccessCallback,
}) => {
  const [quantity, setQuantity] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isOpen && record) {
      setQuantity(record.quantityFound?.toString() || "");
    }
  }, [isOpen, record]);

  const patchRecordMutation = useMutation(
    inventoryClient.patchCompletedStockCountRecord,
    {
      onSuccess: () => {
        // Invalidate the stock count query to refetch the data
        queryClient.invalidateQueries([
          "stock-count",
          { establishmentId, stockCountId },
        ]);
        closeModal();
        // Call success callback if provided
        if (onSuccessCallback) {
          onSuccessCallback();
        }
      },
      onError: (error) => {
        console.error("Error updating stock count record:", error);
      },
    }
  );

  const handleQuantityChange = (e) => {
    // Only allow numbers, dots, and commas
    const sanitized = e.target.value.replace(/[^0-9.,]/g, "");
    setQuantity(sanitized);
  };

  const handleSave = () => {
    if (!quantity || quantity === "") {
      return;
    }

    patchRecordMutation.mutate({
      establishmentId,
      stockCountId,
      recordId: record.id,
      quantityFound: {
        value: parseFloat(quantity),
      },
    });
  };

  const closeModal = () => {
    handleCloseEditQuantityModal();
    setQuantity("");
  };

  const canSave = quantity && quantity !== "" && parseFloat(quantity) >= 0;

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="edit-quantity-modal"
      aria-describedby="edit-stock-count-record-quantity"
    >
      <Box sx={modalBoxStyle}>
        {patchRecordMutation.isLoading ? (
          <Grid
            container
            direction="row"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          <>
            <Grid
              container
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 4,
              }}
            >
              <Grid item>
                <Typography variant="h4">
                  {record?.historicalProductName || "Product Name"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {record?.historicalSizingDisplay || "Product Format"}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  sx={{ backgroundColor: "transparent" }}
                  onClick={closeModal}
                >
                  <CloseOutlinedIcon />
                </IconButton>
              </Grid>
            </Grid>

            {patchRecordMutation.isError && (
              <Box sx={{ marginBottom: 3 }}>
                <Alert severity="error">
                  {patchRecordMutation.error?.response?.data?.message ||
                    patchRecordMutation.error?.message ||
                    "Failed to update quantity. Please try again."}
                </Alert>
              </Box>
            )}

            <Box sx={{ marginBottom: 4 }}>
              <Typography variant="h6" sx={{ marginBottom: 2 }}>
                Total Establishment Count:
              </Typography>
              <TextField
                fullWidth
                value={quantity}
                onChange={handleQuantityChange}
                placeholder="0.00"
                variant="outlined"
                autoFocus
                inputProps={{
                  inputMode: "numeric",
                  style: { textAlign: "center", fontSize: "1.2rem" },
                }}
                sx={{
                  "& legend": { display: "none" },
                  "& fieldset": { top: 0 },
                  "& .MuiOutlinedInput-root": {
                    background: (theme) => theme.palette.pure.white,
                  },
                  "& input::-webkit-clear-button, & input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>

            <Box sx={{ marginTop: 4 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSave}
                disabled={!canSave || patchRecordMutation.isLoading}
                sx={{
                  height: "48px"
                }}
              >
                Save Total
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default EditStockCountRecordQuantityModal;
